import React from 'react'
import { RefreshControl, ScrollView, SectionList, Text, TouchableOpacity, View, useWindowDimensions } from 'react-native'
import type { GameResponse } from '@warcarp/api-types'
import { GameRow, GameSkeletonRow } from '../GameRow'
import { Button } from '../ui/Button'
import { homeGameGroups } from '../../lib/homeGameGroups'
import { PAGE_MAX_WIDTH, PAGE_SIDE_PADDING } from '../../lib/pageLayout'

/**
 * Two-column home sizing. Tablet widths (720–1023) keep the tight 16 px
 * padding with a 280 px command column. Desktop (>= 1024) gets a wider
 * column and real side gutters so the game list does not stretch edge to edge.
 * The header uses HOME_DESKTOP_MAX_WIDTH too, so both stay aligned.
 */
export const HOME_DESKTOP_MAX_WIDTH = PAGE_MAX_WIDTH
export const HOME_SIDEBAR_WIDTH = { tablet: 280, desktop: 320 } as const
export const HOME_SIDE_PADDING = { tablet: PAGE_SIDE_PADDING.tablet, desktop: PAGE_SIDE_PADDING.desktop } as const
/**
 * With no open games there is no right column: the game list goes single,
 * centred and capped at this width, so it never stretches edge to edge and
 * there is never an empty column beside it.
 */
export const HOME_SINGLE_COLUMN_MAX_WIDTH = 820

export function DesktopCommandColumn({ games, completed, currentUserId, openGames, openGameCount, hasMoreOpenGames = false,
  tabs, loading = false, refreshing = false, onRefresh, onGamePress, onNewGame, onJoinGame, onScrollOffsetChange, sample = false,
}: { games: GameResponse[]; completed: boolean; currentUserId?: string; openGames: GameResponse[]
  openGameCount?: number; hasMoreOpenGames?: boolean; tabs: React.ReactNode; loading?: boolean; refreshing?: boolean; onRefresh?: () => void
  onGamePress: (game: GameResponse) => void; onNewGame: () => void; onJoinGame: () => void; sample?: boolean; onScrollOffsetChange?: (offset: number) => void }) {
  const { width: windowWidth } = useWindowDimensions()
  const [width, setWidth] = React.useState(windowWidth)
  const tablet = width < 1024
  const openCount = openGameCount ?? openGames.length
  const openCountLabel = `${openCount}${openGameCount === undefined && hasMoreOpenGames ? '+' : ''}`
  const sections = React.useMemo(() => homeGameGroups(games, completed, currentUserId), [games, completed, currentUserId])
  const sidebarWidth = tablet ? HOME_SIDEBAR_WIDTH.tablet : HOME_SIDEBAR_WIDTH.desktop
  const hasSidebar = openCount > 0
  const row = (game: GameResponse) => <GameRow game={game} currentUserId={currentUserId}
    showMapPreview trackUnread={!sample} loadMapFromNetwork={!sample} onPress={() => onGamePress(game)} />
  return <View onLayout={event => setWidth(event.nativeEvent.layout.width)} style={{ flex: 1, paddingHorizontal: tablet ? HOME_SIDE_PADDING.tablet : HOME_SIDE_PADDING.desktop, paddingTop: 24 }}>
    <View testID={hasSidebar ? 'home-two-column' : 'home-single-column'}
      style={{ flex: 1, width: '100%', maxWidth: hasSidebar ? HOME_DESKTOP_MAX_WIDTH : HOME_SINGLE_COLUMN_MAX_WIDTH, alignSelf: 'center', gap: 20 }}>
      <View testID="home-desktop-actions" style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
        {tabs}
        <View style={{ width: sidebarWidth, flexDirection: 'row', gap: 10 }}>
          <View style={{ flex: 1 }}><Button title="New Game" size="sm" style={{ minHeight: 40, paddingHorizontal: 8 }} textColor="#fff" onPress={onNewGame} /></View>
          <View style={{ flex: 1 }}><Button title="Join Game" size="sm" style={{ minHeight: 40, paddingHorizontal: 8 }} variant="secondary" textClassName="font-stencil tracking-wider" onPress={onJoinGame} /></View>
        </View>
      </View>
      <View style={{ flex: 1, minHeight: 0, flexDirection: 'row', gap: tablet ? 16 : 22 }}>
        <View style={{ flex: 1, minWidth: 0 }}>
        <SectionList style={{ flex: 1, minWidth: 0 }} sections={sections} keyExtractor={game => game.id}
          onScroll={event => onScrollOffsetChange?.(event.nativeEvent.contentOffset.y)} scrollEventThrottle={16}
          stickySectionHeadersEnabled={false} renderItem={({ item }) => row(item)}
          renderSectionHeader={({ section }) => section.data.length ? <Text className="font-stencil text-olive-up text-xs uppercase" style={{ height: 20, marginBottom: 6 }}>{section.title} · {section.data.length}</Text> : null}
          ItemSeparatorComponent={() => <View style={{ height: 12 }} />}
          SectionSeparatorComponent={({ leadingItem }) => <View style={{ height: leadingItem ? 22 : 0 }} />}
          contentContainerStyle={{ paddingBottom: 24 }}
          refreshControl={onRefresh ? <RefreshControl refreshing={refreshing} onRefresh={onRefresh} /> : undefined}
          ListEmptyComponent={loading ? <View className="gap-3">{[0, 1, 2, 3].map(index => <GameSkeletonRow key={index} />)}</View>
            : <View className="py-12 gap-2"><Text className="font-typed text-ink-soft text-base">{completed ? 'No completed games yet.' : 'No games yet.'}</Text><Text className="font-typed text-ink-soft text-sm">Create or join a campaign using the buttons above.</Text></View>} />
        </View>
        {hasSidebar && <ScrollView onScroll={event => onScrollOffsetChange?.(event.nativeEvent.contentOffset.y)} scrollEventThrottle={16} style={{ width: sidebarWidth, flexGrow: 0 }} contentContainerStyle={{ paddingBottom: 24 }}>
          <View testID="home-open-games" style={{ gap: 6 }}>
            <TouchableOpacity accessibilityRole="link" accessibilityLabel={`Open games (${openCountLabel}) — browse all`}
              onPress={onJoinGame} hitSlop={{ top: 12, bottom: 12, left: 0, right: 0 }}
              style={{ minHeight: 20, justifyContent: 'center' }}>
              <Text className="font-stencil text-olive-up text-xs uppercase">Open games · {openCountLabel} →</Text>
            </TouchableOpacity>
            <View style={{ gap: 12 }}>{openGames.slice(0, 3).map(game => <View key={game.id}>{row(game)}</View>)}</View>
          </View>
        </ScrollView>}
      </View>
    </View>
  </View>
}
